import React, { useState, useRef } from "react";
import styled from "styled-components";
import Compressor from "compressorjs";
import * as Sentry from "@sentry/react";
import ErrorText from "../library/ErrorText";
import RecruiterUser from "../../apiResources/RecruiterUser";
import EmployerUser from "../../apiResources/EmployerUser";
import SettingLabel from "./SettingLabel";
import { useDidMountEffect } from "../../hooks";
import { fileToBase64 } from "../../helpers";
import Button from "../library/Button";

type FileData = {
  data: string;
  filename: string;
};

const PhotoSettingOption = <User extends RecruiterUser | EmployerUser>(
  props: Props<User>
) => {
  const { user, onUserChange, disabled } = props;

  const fileInputRef = useRef<HTMLInputElement>();

  const [photo, setPhoto] = useState<FileData>(null);
  const [error, setError] = useState<string>(null);
  const [isLoading, setIsLoading] = useState(false);

  const clearError = () => setError(null);

  const updateUser = async () => {
    setIsLoading(true);
    clearError();

    try {
      user.assignAttributes({
        photo: photo,
      });
      const success = await user.save();
      if (success) {
        onUserChange?.(user);
      } else {
        setError(user.errors.photo?.fullMessage);
      }
    } catch (err) {
      Sentry.captureException(error);
      setError("Oops, something went wrong updating your photo");
    } finally {
      setIsLoading(false);
    }
  };

  useDidMountEffect(() => {
    if (!error) {
      void updateUser();
    }
  }, [photo]);

  const onChange = async (file: File) => {
    const fiveMegabytes = 5000000;
    if (file.size > fiveMegabytes) {
      setError("Please upload an image that is less than 5MB");
      return;
    }

    new Compressor(file, {
      quality: 0.8,
      maxHeight: 500,
      minHeight: 130,
      success: async (compressedFile) => {
        const data = (await fileToBase64(compressedFile)) as string;
        setPhoto({ data, filename: file.name });
      },
    });
  };

  return (
    <section>
      <Header>
        <SettingLabel>Photo</SettingLabel>
      </Header>
      <Row>
        <ProfileImage src={user?.avatar} />
        <HiddenUploadInput
          ref={fileInputRef}
          accept="image/*"
          type="file"
          onChange={(event) => onChange(event.target.files[0])}
        />
        <Button
          secondary
          type="button"
          disabled={disabled || isLoading}
          onClick={() => fileInputRef.current.click()}
        >
          {isLoading ? "Uploading..." : "Upload"}
        </Button>
      </Row>
      {error && <ErrorText>{error}</ErrorText>}
    </section>
  );
};

type Props<User> = {
  user: User;
  isEditing?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onEdit: () => void;
  onDone: () => void;
  onUserChange?: (user: User) => void;
};

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Header = styled(Row)`
  margin-bottom: 1rem;
`;

const ProfileImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin-right: 1.5rem;
`;

const HiddenUploadInput = styled.input`
  &&& {
    display: none;
  }
`;

export default PhotoSettingOption;
